/**
 * Tallinn's KAR TANESİ — the snowflake — in the shared 120×120 LINE asset box.
 *
 * The owner's call for three assets at once (2026-08-14): the Home tab, all four
 * passcode dots and the chat wallpaper. That is the shape this family likes best
 * — Sakura's blossom, Rio's parasol — because a theme whose bar glyph, lock
 * screen and wallpaper are one object reads as one thing.
 *
 * WHAT IS LOAD-BEARING:
 *
 *  - SIX ARMS, and only six. A snowflake's whole identity is hexagonal symmetry;
 *    at eight it is a compass rose and at four it is a plus sign.
 *  - The ARMS ARE 7 UNITS THICK and the side branches 4.6. LINE renders a
 *    passcode dot at roughly 30px and a classic tab glyph at 22, so one unit is a
 *    quarter of a pixel: the 4-unit arms this started on came out at a pixel wide
 *    and the whole flake turned to grey lint. Real snow crystals are far finer
 *    than this and drawing them honestly is what makes them vanish.
 *  - The BRANCHES SIT AT TWO STATIONS, at 46% and 74% along each arm, and the
 *    outer pair is shorter. One station reads as a cross-hatch; three closes the
 *    gaps between arms into a disc.
 *  - The BRANCHES SWEEP FORWARD, 48° off the arm rather than 90°. Perpendicular
 *    branches make a grid; swept ones make a crystal.
 *  - The HUB IS A HEXAGON, not a circle. It is two pixels across at tab size and
 *    nobody will ever see its corners, but it is what makes the six arms meet
 *    cleanly instead of piling into a blob.
 *
 * Every subpath is wound clockwise so a nonzero fill unions the overlaps rather
 * than punching holes ([[line-shared-glyph-modules]]) — enforced by `cw()`, not
 * by eye, because a rotated bar's winding flips with its direction.
 *
 * The fit: the flake spans the full 120 box across its widest diagonal and is
 * centred on (60, 60). Unlike the towers and statues in this family it has no
 * "up", which is why the wallpaper is free to tumble it.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/** A thick bar from one point to another, as a clockwise quad. */
function bar( x1: number, y1: number, x2: number, y2: number, t: number ): string {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot( dx, dy ) || 1;
  const nx = -dy / len * t / 2, ny = dx / len * t / 2;
  return cw( [
    [ x1 + nx, y1 + ny ], [ x2 + nx, y2 + ny ],
    [ x2 - nx, y2 - ny ], [ x1 - nx, y1 - ny ],
  ] );
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60, CY = 60;
/** How far an arm reaches from the hub. */
const R = 52;
/** Arm and branch thickness. */
const ARM_W = 7, BR_W = 4.6;
/** Where the branches leave the arm, as a fraction of its length, and how long
 *  each pair is. The inner pair is the long one — that is what a real crystal
 *  does, and it is also what keeps the outline from reading as a star. */
const STATIONS: [ number, number ][] = [ [ 0.46, 21 ], [ 0.74, 14 ] ];
/** How far the branches sweep toward the tip, in radians off the arm. */
const SWEEP = 48 * Math.PI / 180;

/** One arm at `a` radians: the spine, its four branches, nothing else. */
function arm( a: number ): string {
  const ux = Math.cos( a ), uy = Math.sin( a );
  const tipX = CX + ux * R, tipY = CY + uy * R;
  const parts = [ bar( CX, CY, tipX, tipY, ARM_W ) ];
  for ( const [ at, len ] of STATIONS ) {
    const bx = CX + ux * R * at, by = CY + uy * R * at;
    for ( const side of [ 1, -1 ] ) {
      const ang = a + side * SWEEP;
      parts.push( bar( bx, by, bx + Math.cos( ang ) * len, by + Math.sin( ang ) * len, BR_W ) );
    }
  }
  return parts.join( ' ' );
}

/** The hub: a small hexagon the six arms meet in. */
export const SNOW_HUB = cw(
  Array.from( { length: 6 }, ( _, i ): [ number, number ] => {
    const a = ( i / 6 ) * Math.PI * 2;
    return [ CX + Math.cos( a ) * 8.5, CY + Math.sin( a ) * 8.5 ];
  } ),
);

/** The six arms with their branches. */
export const SNOW_ARMS = Array.from( { length: 6 }, ( _, i ) => arm( ( i / 6 ) * Math.PI * 2 ) )
  .join( ' ' );

/** The whole flake as ONE path — arms, branches and hub. Every subpath is
 *  clockwise, so a nonzero fill unions them into one silhouette. */
export const SNOWFLAKE = `${ SNOW_ARMS } ${ SNOW_HUB }`;

/**
 * A second, SIMPLER flake for the wallpaper's smallest grains: the six arms with
 * one station of branches instead of two.
 *
 * It exists because the full flake is drawn at about a third of this box on the
 * chat background, where the outer branches land under a pixel and fill in — a
 * flake that has turned into a disc is worse than one drawn with fewer branches.
 */
export const SNOWFLAKE_SIMPLE = ( () => {
  const parts: string[] = [ SNOW_HUB ];
  for ( let i = 0; i < 6; i++ ) {
    const a = ( i / 6 ) * Math.PI * 2;
    const ux = Math.cos( a ), uy = Math.sin( a );
    parts.push( bar( CX, CY, CX + ux * R, CY + uy * R, ARM_W + 1 ) );
    const bx = CX + ux * R * 0.55, by = CY + uy * R * 0.55;
    for ( const side of [ 1, -1 ] ) {
      const ang = a + side * SWEEP;
      parts.push( bar( bx, by, bx + Math.cos( ang ) * 20, by + Math.sin( ang ) * 20, BR_W + 1 ) );
    }
  }
  return parts.join( ' ' );
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here, not in the passcode component: line-lock (the preview) and
 * line-page (the exported PNGs) both read them, and every time this family kept
 * two copies of a colour they drifted.
 *
 * Measured on the skin the owner picked (2026-08-14 22.06), whose passcode screen
 * puts the WHITE half on top. So these are DARK — the ice-whites written against
 * the borrowed cyan skin were the exact inverse and would have been invisible.
 */

/**
 * The tilt of each of the four dots, in degrees about the flake's own centre
 * (owner 2026-08-14: "kar taneleri passcode'da sağa sola biraz eğik olacak").
 *
 * They alternate side and they stay SMALL, and the small part is not caution: a
 * six-armed flake maps onto itself every 60 degrees, so a tilt only reads as a
 * tilt while it is well inside that. At 18 degrees the flake is a third of the
 * way to being the same picture again, which is why nothing here goes past it.
 *
 * A flake turns about its middle, unlike the parasol's poses, which pivot where
 * the pole enters the sand. It is falling, not planted.
 */
export const SNOW_PC_TILTS = [ -13, 9, -7, 16 ];

/**
 * The four entered flakes: the theme's blues, walking from a pale limestone to a
 * mid harbour blue.
 *
 * SOFT, and that took two passes. The first run ended on Deep Cobalt and measured
 * 1.97 / 4.36 / 5.96 / 9.60 on the white — a legible ramp, and far too heavy for
 * what it is drawing (owner 2026-08-14: "2. çok koyu oldu ... 4 de çok koyu").
 * Snow is a pale thing; a lock screen of near-black flakes reads as ink blots.
 * This run is 1.74 / 2.09 / 2.66 / 3.78, which keeps every step visible while the
 * deepest one still looks like snow rather than like a logo.
 */
export const SNOW_PC_COLORS = [ '#B4C6E0', '#93B7DE', '#6FA3D8', '#4F86C6' ];

/**
 * ONE tone for every unentered flake — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * A muted WARM GREY, and the warmth is the point (owner 2026-08-14: "OFF
 * passcodelar çok silik ve ON'lara çok benziyor ... biraz daha soluk gri kızıla
 * kaçan"). Every earlier attempt separated the two states by LIGHTNESS alone,
 * inside one blue family, so a pale blue flake beside a mid blue one read as the
 * same flake at two exposures. This one differs by HUE, which is a difference the
 * eye takes at a glance and does not have to measure — and the hue it borrows is
 * the theme's own terracotta, desaturated to the point where it is a grey with a
 * red cast rather than a pink.
 *
 * 1.81:1 on the white: a step ABOVE the 1.26 it replaced, because that one was
 * also simply too faint, and still well under the palest entered flake's 1.74 in
 * apparent weight once the hue difference is doing the separating.
 */
export const SNOW_PC_OFF = '#CFBCBC';
